import React, { useEffect, useRef } from "react"
import { observer } from "mobx-react"
import {
  captureDemoCheckoutOpen,
  captureDemoCheckoutProgress,
  type DemoCheckoutLocation,
  type DemoCheckoutStep
} from "../../analytics"
import type { ShopStore, ShopStage } from "../stores/shop/ShopStore"
import { CheckoutStage } from "./shop/CheckoutStage"
import { ShopPanel } from "./shop/ShopPanel"

// The shop tree is a verbatim copy of the library example, so measurement
// stays at this site-owned wrapper. A click arms the event, but it is emitted
// only after the order request succeeds and the shop actually opens checkout.
// Resumed/bookmarked checkouts never count as demo_checkout_open, and report
// no demo_checkout_progress either: progress belongs to the order this page
// opened by a click, once per step. By the time checkout opens the shop has
// already pushed /checkout/<reference>, so the click also records the page it
// came from: the X conversion is sent that page, never the checkout URL.
export const TrackedDemoShop: React.FC<{
  shop: ShopStore
  sourceLocation: DemoCheckoutLocation
}> = observer(({ shop, sourceLocation }) => {
  const requestedByClick = useRef(false)
  const openedFromPathname = useRef<string | undefined>(undefined)
  const previousStage = useRef<ShopStage>(shop.stage)
  const trackedOrder = useRef("")
  const reportedSteps = useRef(new Set<DemoCheckoutStep>())
  const pickerKey = shop.checkout.pickerKey
  const invoiceShown = Boolean(shop.checkout.displayInvoice)
  const paid = shop.orderPaid

  useEffect(() => {
    if (
      requestedByClick.current &&
      previousStage.current !== "checkout" &&
      shop.stage === "checkout"
    ) {
      captureDemoCheckoutOpen(sourceLocation, openedFromPathname.current)
      requestedByClick.current = false
      openedFromPathname.current = undefined
      trackedOrder.current = shop.orderReference
      reportedSteps.current = new Set()
    } else if (requestedByClick.current && shop.stage === "catalog" && !shop.placingOrder) {
      // The order request failed or was abandoned; no checkout opened.
      requestedByClick.current = false
      openedFromPathname.current = undefined
    }
    previousStage.current = shop.stage
  }, [shop.placingOrder, shop.stage, sourceLocation])

  useEffect(() => {
    if (!trackedOrder.current || trackedOrder.current !== shop.orderReference) return
    const report = (step: DemoCheckoutStep) => {
      if (reportedSteps.current.has(step)) return
      reportedSteps.current.add(step)
      captureDemoCheckoutProgress(step, pickerKey, sourceLocation)
    }
    if (pickerKey) report("method_open")
    if (invoiceShown) report("invoice_shown")
    if (paid) report("paid")
  }, [pickerKey, invoiceShown, paid, shop.orderReference, sourceLocation])

  const armExplicitCheckout = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target instanceof Element ? event.target : null
    const button = target?.closest(".or-shop-footer button")
    if (!(button instanceof HTMLButtonElement) || button.disabled || shop.stage !== "catalog") return
    requestedByClick.current = true
    openedFromPathname.current = window.location.pathname
  }

  return (
    <div onClickCapture={armExplicitCheckout}>
      <ShopPanel
        shop={shop}
        renderCheckout={() => <CheckoutStage shop={shop} />}
      />
    </div>
  )
})
